import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import TopBarHeader from '../../components/ui/TopBarHeader';
import { useRestaurantContext } from '../../context/RestaurantContext';
import { getQueueHistory } from '../../services/api/restApiAdapter';
import MoneyUtils from '../../utils/moneyUtils';

const QueueHistory = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const { theme } = useRestaurantContext();
  
  const [history, setHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const observer = useRef();
  const loadingRef = useRef(false);

  const themeColor = theme?.secondary || '#000';
  const themeTextColor = theme?.primary || '#fff';

  const fetchHistory = useCallback(async (pageNum, isNew = false) => {
    if (loadingRef.current || (!hasMore && !isNew)) return;
    
    loadingRef.current = true;
    setIsLoading(true);
    try {
      const response = await getQueueHistory(restaurantId, pageNum, 20);
      if (response.success) {
        const newData = response.data || [];
        setHistory(prev => isNew ? newData : [...prev, ...newData]);
        setHasMore(newData.length === 20 && response.pagination.page < response.pagination.pages);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      loadingRef.current = false;
      setIsLoading(false);
      setIsInitialLoad(false);
    }
  }, [restaurantId, hasMore]);

  useEffect(() => {
    fetchHistory(1, true);
  }, []); // Run once on mount

  const lastElementRef = useCallback(node => {
    if (loadingRef.current) return;
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        setPage(prevPage => {
          const nextPage = prevPage + 1;
          fetchHistory(nextPage);
          return nextPage;
        });
      }
    });
    if (node) observer.current.observe(node);
  }, [hasMore, fetchHistory]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-50 text-green-600 border-green-100';
      case 'cancelled': return 'bg-red-50 text-red-600 border-red-100';
      case 'called': return 'bg-blue-50 text-blue-600 border-blue-100';
      default: return 'bg-gray-50 text-gray-600 border-gray-100';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }).format(date);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <TopBarHeader 
        title="Queue History" 
        showBack 
        onBackClick={() => navigate(`/restaurant/${restaurantId}/queue-status`)}
        hideCart
        hideMenu
      />

      <div className="flex-1 overflow-y-auto p-4 space-y-3 max-w-md mx-auto w-full">
        {isInitialLoad && isLoading ? (
          <div className="flex flex-col items-center justify-center py-20 space-y-4">
            <Icon name="Loader" className="animate-spin text-gray-300" size={32} />
            <p className="text-xs font-black text-gray-400 uppercase tracking-widest">Loading history...</p>
          </div>
        ) : history.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-10 text-center">
            <div className="w-16 h-16 bg-white rounded-2xl shadow-sm border border-gray-100 flex items-center justify-center mb-6 text-gray-200">
              <Icon name="History" size={32} />
            </div>
            <h3 className="text-lg font-black text-gray-900 mb-2">No History Yet</h3>
            <p className="text-sm text-gray-400 leading-relaxed font-medium">
              Your previous queue entries will appear here once they are completed or cancelled.
            </p>
          </div>
        ) : (
          <>
            {history.map((entry, index) => (
              <motion.div 
                key={entry.id || entry._id || index}
                ref={index === history.length - 1 ? lastElementRef : null}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-2xl p-4 border border-gray-100 shadow-sm flex flex-col gap-3"
              >
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-gray-900 text-white flex flex-col items-center justify-center shadow-sm"
                         style={{ backgroundColor: themeColor, color: themeTextColor }}>
                      <span className="text-[7px] font-black uppercase opacity-60">Code</span>
                      <span className="text-lg font-black leading-none">{entry.queueCode}</span>
                    </div>
                    <div>
                      <p className="text-sm font-black text-gray-900">{formatDate(entry.createdAt)}</p>
                      <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mt-0.5">
                        {formatTime(entry.createdAt)} • {entry.partySize} Guests
                      </p>
                    </div>
                  </div>
                  <div className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase tracking-wider border ${getStatusColor(entry.status)}`}>
                    {entry.status}
                  </div>
                </div>

                {entry.selfOrderCart?.items?.length > 0 && (
                  <div className="pt-3 border-t border-gray-50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Icon name="ShoppingBag" size={14} className="text-gray-300" />
                      <span className="text-[10px] font-bold text-gray-500">
                        {entry.selfOrderCart.items.length} items pre-ordered
                      </span>
                    </div>
                    <span className="text-xs font-black text-gray-900">
                      {MoneyUtils.format(entry.selfOrderCart.total)}
                    </span>
                  </div>
                )}
              </motion.div>
            ))}
            
            {isLoading && !isInitialLoad && (
              <div className="flex justify-center py-4">
                <Icon name="Loader" className="animate-spin text-gray-300" size={20} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default QueueHistory;
